import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {ContextualMenuDropdownOption} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {FancyTextList} from '@Components/fancy-text-list';
import {getSortedFancyTextArrayFromHashmap} from '@Libraries/fancy-text-library';
import {FancyTextType} from '@Components/fancy-text-list/fancy-text-list.types';
import {DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {usePopulateFancyTextData} from '@Hooks/poster-editor/usePopulateFancyTextData';
import {useAppSelector} from '@/hooks';
import styles from './fancy-text-style-option.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromFancyTextItem} from '@Hooks/poster-editor/useGetKeyFromFancyTextItem';

export function FancyTextStyleOption(): ReactElement | null {
  const [isFontBeingApplied, setIsFontBeingApplied] = useState(false);
  const hasEffectsPopulated = usePopulateFancyTextData();
  const fancyTextHashmap = useAppSelector((state) => {
    return state.fancyText.data.fancyTextEffectHashmap;
  });
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const idFancyText = useGetKeyFromFancyTextItem('idFancyText');

  if (!activeItem) {
    throw new Error('Active item is null');
  }

  if (!activeItem.isFancyText() || idFancyText === undefined) {
    throw new Error(`Expected active item type to be fancy text, instead got ${activeItem.gitype}`);
  }

  const getCustomPopUp = (): ReactElement => {
    return (
      <FancyTextList
        className={`${styles.list} spacing-m-0 `}
        data={{
          type: FancyTextType.STYLE,
          fancyTextStyles: getSortedFancyTextArrayFromHashmap(fancyTextHashmap),
        }}
        onClick={(id: string): void => {
          if (idFancyText === id) {
            return;
          }
          setIsFontBeingApplied(true);
          void activeItem.updateFancyTextEffect(id, fancyTextHashmap[id].defaultColors).finally((): void => {
            setIsFontBeingApplied(false);
          });
        }}
        selectedId={idFancyText}
        doScrollIntoView
      />
    );
  };

  return (
    <ContextualMenuDropdownOption
      customPopup={getCustomPopUp()}
      id="fancy-text-style-option"
      label={window.i18next.t('pmwjs_style')}
      dropdownSelectorText={!hasEffectsPopulated || Object.keys(fancyTextHashmap).length === 0 ? '' : fancyTextHashmap[idFancyText].name}
      isLoading={!hasEffectsPopulated || Object.keys(fancyTextHashmap).length === 0 || isFontBeingApplied}
      isDisabled={isFontBeingApplied}
      position={DROPDOWN_POSITION.FIXED}
    />
  );
}
